/**
 * Cookie utility functions for managing browser cookies
 */

/**
 * Get a cookie value by name
 * @param name - The name of the cookie
 * @returns The cookie value or null if not found
 */
export function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null;
}

/**
 * Set a cookie with the given name and value
 * @param name - The name of the cookie
 * @param value - The value to set
 * @param options - Cookie options
 */
export function setCookie(
  name: string,
  value: string,
  options: {
    path?: string;
    maxAge?: number;
    sameSite?: 'Strict' | 'Lax' | 'None';
    secure?: boolean;
  } = {},
): void {
  const { path = '/find-care', maxAge = 3600, sameSite = 'Lax', secure = false } = options;

  let cookieString = `${name}=${value}; path=${path}; max-age=${maxAge}; SameSite=${sameSite}`;

  if (secure) {
    cookieString += '; Secure';
  }

  document.cookie = cookieString;
}

/**
 * Clear a cookie by setting its expiration to the past
 * @param name - The name of the cookie to clear
 * @param path - The path of the cookie (should match the path it was set with)
 */
export function clearCookie(name: string, path = '/find-care'): void {
  document.cookie = `${name}=; path=${path}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
}

/**
 * Clear the app_state cookie specifically
 */
export function clearAppStateCookie(): void {
  clearCookie('app_state');
  clearCookie('legacy_state');
}

/**
 * Set the app_state cookie with a state code
 * @param stateCode - The state code to set in the cookie
 */
export function setAppStateCookie(stateCode: string): void {
  clearCookie('app_state');
  setCookie('legacy_state', stateCode);
}

/**
 * Get the app_state cookie value
 * @returns The app_state value or null if not found
 */
export function getAppStateCookie(): string | null {
  return getCookie('legacy_state');
}
